import { apiClient } from "@/shared/services/api";
import type { ApiResponse } from "@/features/auth/services/authApi";

// ── Types ──────────────────────────────────────────────────────────────────

export interface TransportRateDto {
  id: string;
  destinationName: string;
  region?: string;
  costAmount: number;
  notes?: string;
  isActive: boolean;
  destinationLatitude?: number;
  destinationLongitude?: number;
  geoProvider?: string;
  lastGeocodedAt?: string;
  isMapped: boolean;
  deliveryAreas: Array<{ id: string; name: string }>;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateTransportRateRequest {
  destinationName: string;
  region?: string;
  costAmount: number;
  notes?: string;
  destinationLatitude?: number;
  destinationLongitude?: number;
  deliveryAreas?: string[];
}

export interface UpdateTransportRateRequest {
  id: string;
  destinationName: string;
  region?: string;
  costAmount: number;
  notes?: string;
  isActive: boolean;
  destinationLatitude?: number;
  destinationLongitude?: number;
  deliveryAreas?: string[];
}

export interface TransportRateListResult {
  items: TransportRateDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

// ── API ────────────────────────────────────────────────────────────────────

export const transportRatesApi = {
  getPaged: async (pageNumber: number, search: string): Promise<TransportRateListResult> => {
    const res = await apiClient.get<ApiResponse<TransportRateListResult>>("/transportrates/paged", {
      params: { pageNumber, pageSize: 25, search: search || undefined },
    });
    return res.data.data;
  },
  getAll: async (): Promise<TransportRateDto[]> => {
    const res = await apiClient.get<ApiResponse<TransportRateDto[]>>("/transportrates");
    return res.data.data ?? [];
  },

  getActive: async (): Promise<TransportRateDto[]> => {
    const res = await apiClient.get<ApiResponse<TransportRateDto[]>>("/transportrates/active");
    return res.data.data ?? [];
  },

  getByDestination: async (destinationName: string): Promise<TransportRateDto | null> => {
    const res = await apiClient.get<ApiResponse<TransportRateDto | null>>(
      "/transportrates/by-destination",
      { params: { destinationName } }
    );
    return res.data.data ?? null;
  },

  create: async (data: CreateTransportRateRequest): Promise<TransportRateDto> => {
    const res = await apiClient.post<ApiResponse<TransportRateDto>>("/transportrates", data);
    return res.data.data;
  },

  update: async (id: string, data: UpdateTransportRateRequest): Promise<TransportRateDto> => {
    const res = await apiClient.put<ApiResponse<TransportRateDto>>(`/transportrates/${id}`, {
      ...data,
      id,
    });
    return res.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/transportrates/${id}`);
  },
};
